import { yupResolver } from '@hookform/resolvers/yup';
import { startOfDay, nextFriday, addDays } from 'date-fns';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  EmptyEnquiry,
  enquiryDtoStrictValidation,
  IEnquiryDto,
  IEnquiryOptionDto,
} from '../model';
import { DateOnly } from '../utility';
import { useTranslation } from 'react-i18next';
import {
  FormGroup,
  StyledButton,
  StyledCheckButton,
  StyledInput,
  StyledTextarea,
} from '../ui';
import AddressForm from './address-form';
import { IEnquiryState } from './state';
import { EnvelopeIcon, PhoneIcon } from '@heroicons/react/24/outline';

interface IFormProps {
  enquiryState: IEnquiryState;
  roomOptions: IEnquiryOptionDto[];
  treatmentOptions: IEnquiryOptionDto[];
  pricingOptions: IEnquiryOptionDto[];
  code?: string | null;

  onSubmit?: (data: IEnquiryDto) => Promise<unknown>;
}

export function Form(props: IFormProps) {
  const { t, i18n } = useTranslation();

  const { control, handleSubmit, reset, formState } = useForm<IEnquiryDto>({
    resolver: yupResolver(enquiryDtoStrictValidation(t)),
    defaultValues: {
      ...EmptyEnquiry,
      from: new DateOnly(nextFriday(startOfDay(new Date()))),
      to: new DateOnly(addDays(nextFriday(startOfDay(new Date())), 10)),
      personCount: 1,
      contactLanguage: i18n.language,
      roomOptions: props.roomOptions,
      treatmentOptions: props.treatmentOptions,
      pricingOptions: props.pricingOptions,
      promoCode: (props.code === '' ? null : props.code) ?? null,
    },
    mode: 'onChange',
  });

  const treatments = useFieldArray({ control, name: 'treatmentOptions' });
  const rooms = useFieldArray({ control, name: 'roomOptions' });
  const pricingOptions = useFieldArray({ control, name: 'pricingOptions' });

  return (
    <form
      onSubmit={handleSubmit((data) => {
        if (props.onSubmit) {
          return props.onSubmit(data).then(() => reset(data));
        }
      })}
    >
      <div className="flex flex-col gap-3 mt-4 mb-28">
        <FormGroup title={t('traveldata.title')}>
          <div className="sm:col-span-3">
            <Controller
              name="from"
              control={control}
              render={(s) =>
                StyledInput({
                  ...s,
                  title: t('traveldata.from'),
                  inputType: 'date',
                })
              }
            />
          </div>

          <div className="sm:col-span-3">
            <Controller
              name="to"
              control={control}
              render={(s) =>
                StyledInput({
                  ...s,
                  title: t('traveldata.to'),
                  inputType: 'date',
                })
              }
            />
          </div>

          <div className="sm:col-span-3">
            <Controller
              name="personCount"
              control={control}
              render={(s) =>
                StyledInput({
                  ...s,
                  numberMax: '50',
                  numberMin: '1',
                  numberStepSize: '1',
                  title: t('traveldata.person-count'),
                  inputType: 'number',
                })
              }
            />
          </div>

          <div className="sm:col-span-3">
            <Controller
              name="promoCode"
              control={control}
              render={(s) =>
                StyledInput({
                  ...s,
                  title: t('traveldata.promo'),
                  hint: '(optional)',
                })
              }
            />
          </div>
        </FormGroup>

        <FormGroup title={t('contact.title')}>
          <div className="sm:col-span-3">
            <Controller
              name="contactMail"
              control={control}
              render={(s) =>
                StyledInput({
                  ...s,
                  title: t('contact.mail'),
                  prefix: <EnvelopeIcon className="size-4" />,
                  autoComplete: 'email',
                  inputType: 'email',
                  hint: '*',
                })
              }
            />
          </div>

          <div className="sm:col-span-3">
            <Controller
              name="contactPhone"
              control={control}
              render={(s) =>
                StyledInput({
                  ...s,
                  title: t('contact.call'),
                  prefix: <PhoneIcon className="size-4" />,
                  autoComplete: 'tel',
                  placeholder: '+43',
                  hint: '*',
                })
              }
            />
          </div>
        </FormGroup>

        <FormGroup title={t('personal.title')}>
          <div className="sm:col-span-2">
            <Controller
              name="contactTitle"
              control={control}
              render={(s) =>
                StyledInput({
                  ...s,
                  title: t('personal.salutation'),
                  hint: '(optional)',
                })
              }
            />
          </div>

          <div className="sm:col-span-2">
            <Controller
              name="contactFirstname"
              control={control}
              render={(s) =>
                StyledInput({
                  ...s,
                  title: t('personal.firstname'),
                  hint: '*',
                  autoComplete: 'given-name',
                })
              }
            />
          </div>

          <div className="sm:col-span-2">
            <Controller
              name="contactLastname"
              control={control}
              render={(s) =>
                StyledInput({
                  ...s,
                  title: t('personal.lastname'),
                  hint: '*',
                  autoComplete: 'family-name',
                })
              }
            />
          </div>

          <AddressForm control={control}></AddressForm>
        </FormGroup>

        <FormGroup title={t('callback.title')} isOptional={true}>
          <div className="sm:col-span-3">
            <Controller
              name="callback"
              control={control}
              render={(s) =>
                StyledInput({
                  ...s,
                  title: t('callback.date'),
                  inputType: 'datetime-local',
                  numberStepSize: '900',
                })
              }
            />
          </div>
        </FormGroup>

        <FormGroup title={t('newsletter.title')} isOptional={true}>
          <div className="sm:col-span-6">
            <Controller
              name="newsletter"
              control={control}
              render={(s) => (
                <StyledCheckButton
                  type="button"
                  rootClassName="block w-full"
                  isChecked={s.field.value ?? false}
                  onClick={() => s.field.onChange(!s.field.value)}
                >
                  {t('newsletter.message')}
                </StyledCheckButton>
              )}
            />
          </div>
        </FormGroup>

        <FormGroup title={t('airporttransfer.title')} isOptional={true}>
          <div className="sm:col-span-6">
            <Controller
              name="airportTransfer"
              control={control}
              render={(x) => (
                <StyledCheckButton
                  type="button"
                  rootClassName="block w-full"
                  isChecked={x.field.value ?? false}
                  onClick={() => x.field.onChange(!x.field.value)}
                >
                  {t('airporttransfer.message')}
                </StyledCheckButton>
              )}
            />
          </div>
        </FormGroup>

        <FormGroup
          title={t('treatment.title')}
          addColon={true}
          isOptional={props.treatmentOptions.length > 1}
        >
          <ul className="flex flex-col sm:flex-row flex-wrap *:flex-1 *:min-w-52 gap-2 sm:col-span-6">
            {treatments.fields.map((option, idx) => (
              <li key={option.id}>
                <Controller
                  name={`treatmentOptions.${idx}.valueEnabled`}
                  control={control}
                  render={({ field }) => (
                    <StyledCheckButton
                      type="button"
                      disabled={props.treatmentOptions.length === 1}
                      isChecked={field.value}
                      rootClassName="block w-full"
                      onClick={() => field.onChange(!field.value)}
                    >
                      {option.name}
                    </StyledCheckButton>
                  )}
                />
              </li>
            ))}
          </ul>
        </FormGroup>

        <FormGroup
          title={t('room.title')}
          addColon={true}
          isOptional={props.roomOptions.length > 1}
        >
          <ul className="flex flex-col sm:flex-row flex-wrap *:flex-1 *:min-w-52 gap-2 sm:col-span-6">
            {rooms.fields.map((option, idx) => (
              <li key={option.id}>
                <Controller
                  render={({ field }) => (
                    <StyledCheckButton
                      type="button"
                      rootClassName="block w-full"
                      disabled={props.treatmentOptions.length === 1}
                      isChecked={field.value}
                      onClick={() => field.onChange(!field.value)}
                    >
                      {option.name}
                    </StyledCheckButton>
                  )}
                  name={`roomOptions.${idx}.valueEnabled`}
                  control={control}
                />
              </li>
            ))}
          </ul>
        </FormGroup>

        <div className="sm:col-span-6">
          <FormGroup title={t('message.title')} isOptional={true}>
            <div className="sm:col-span-6">
              <Controller
                name="message"
                control={control}
                render={(s) =>
                  StyledTextarea({
                    ...s,
                    title: '',
                    fieldNameOverride: 'message',
                  })
                }
              />
            </div>
          </FormGroup>
        </div>

        <div className="w-full flex justify-end -mt-12 left-0 bottom-0 p-3 bg-brand-cashmere-50 space-x-2">
          <StyledButton
            design="primary"
            type="submit"
            pulse={formState.isValid}
            disabled={
              formState.isSubmitting ||
              formState.isLoading ||
              !formState.isValid
            }
          >
            {t('action')}
          </StyledButton>
        </div>
      </div>
    </form>
  );
}

export default Form;
